/**
 * SOLID Review
 * S — Acceptable. Bundles several related-but-distinct jobs: TCF payload mapping (`stateOf`), Google
 *     Consent Mode signaling, Funding Choices script injection, a first-party fallback timer, and pub/sub
 *     state broadcasting all live in one class.
 *     Each piece is small and the domain (cookie consent) is genuinely cohesive, but it's doing CMP
 *     wiring, GA wiring, and DOM script injection together.
 * O — Acceptable. Assessed, left as-is (reviewed, not overlooked): adding a new consent source means
 *     adding a branch inside init()/registerTcfListener() rather than plugging in a strategy. This app
 *     runs exactly one CMP vendor (Google Funding Choices/TCF) in production, and has for the project's
 *     whole history — building a pluggable-vendor abstraction now would be speculative generality for a
 *     need that has never materialized, not a fix for a real extension pain point. Revisit if a second CMP
 *     vendor is ever actually integrated.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Implements IGatewayConsent's five methods and nothing more; `getState` and `hasCertifiedCmp` had
 *     no production reader and went in strict-ports FE4b (§29.4b).
 * D — Strong. Implements the application-layer IGatewayConsent port (good direction); the manual-consent
 *     store (P1, §28.286), the page and the timers (P2, §28.287) are handed in — it names no browser global
 *     and reaches into no React/UI state.
 */
import type { IGatewayConsent } from '../../application/consent/IGatewayConsent';
import {
  IEntityCookieConsentCategories,
  IStateCookieConsent,
  INITIAL_CONSENT_STATE,
  GRANTED_CATEGORIES,
} from '../../domain/ports/IStateCookieConsent';
import type { ManualConsentStore } from './ManualConsentStore';
import type { IServiceConsentPage, TCData } from './IServiceConsentPage';
import type { IServiceTimers, TTimer } from '../platform/IServiceTimers';

const FC_SCRIPT_ID = 'google-funding-choices';
/** How long to wait for a CMP before falling back (dev / unconfigured envs). */
const CMP_FALLBACK_MS = 3_000;

/**
 * THE CERTIFIED CMP, BEHIND THE APPLICATION'S CONSENT GATEWAY (Google Funding Choices, FE4b) — built once by the composition root
 * over the page and the timers it is handed (frontend-infrastructure-oo P2, 2026-10-28, §28.287): the window's TCF API and Funding
 * Choices, Google tag's data layer and the script tag are the page's (`IServiceConsentPage`), not globals named here.
 */
export class ConsentGateway implements IGatewayConsent {
  private state: IStateCookieConsent = INITIAL_CONSENT_STATE;
  private readonly listeners = new Set<(state: IStateCookieConsent) => void>();
  private initialized = false;
  private fallbackTimer: TTimer | null = null;

  constructor(
    private readonly manualConsent: ManualConsentStore,
    private readonly page: IServiceConsentPage,
    private readonly timers: IServiceTimers,
    private readonly fundingChoicesPublisherId: string | null = null,
  ) {}

  /**
   * Maps an IAB TCF v2.2 TCData payload onto our Consent Mode categories.
   *
   * Heuristic mapping by standard TCF purpose id:
   *  - adStorage         ← purpose 1  (store/access info on device)
   *  - adPersonalization ← purposes 3 & 4 (create + select personalised ads)
   *  - adUserData        ← purpose 7  (measure ad performance)
   *  - analyticsStorage  ← purposes 8/9/10 (content/market measurement, improve)
   *  - functionality     ← always granted (strictly-necessary bucket)
   *
   * When GDPR does not apply (visitor outside opt-in regions) everything is
   * granted — the geo-aware opt-out model.
   */
  static stateOf(data: TCData): IStateCookieConsent {
    if (data.gdprApplies === false) {
      return {
        resolved: true,
        categories: GRANTED_CATEGORIES,
        region: data.publisherCC ?? 'NON_EEA',
        source: 'US',
        tcString: data.tcString ?? null,
      };
    }

    const c = data.purpose?.consents ?? {};
    const has = (id: number): boolean => c[id] === true;

    const categories: IEntityCookieConsentCategories = {
      adStorage: has(1),
      adPersonalization: has(3) && has(4),
      adUserData: has(7),
      analyticsStorage: has(8) || has(9) || has(10),
      functionality: true,
    };

    return {
      resolved: true,
      categories,
      region: data.publisherCC ?? 'EEA',
      source: 'TCF',
      tcString: data.tcString ?? null,
    };
  }

  /** Decides whether a TCData event represents a final, actionable decision. */
  static isResolved(data: TCData): boolean {
    return data.eventStatus === 'tcloaded' || data.eventStatus === 'useractioncomplete';
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Remember a prior first-party choice across reloads: restore and apply it
    // immediately so the visitor's Accept/Reject is honoured before — and unless —
    // the CMP reports a genuine new decision (see registerTcfListener).
    const persisted = this.manualConsent.load();
    if (persisted) {
      const cookieConsentState: IStateCookieConsent = {
        resolved: true,
        categories: persisted,
        region: 'MANUAL',
        source: 'MANUAL',
        tcString: null,
      };
      this.publish(cookieConsentState);
    }

    this.injectFundingChoices();
    this.registerTcfListener();

    // Fallback: if no CMP answers (unconfigured / dev) and we had no stored choice
    // to restore, unblock with the opt-out default so the app remains usable.
    if (!this.state.resolved) {
      this.fallbackTimer = this.timers.setTimeout(() => {
        /* c8 ignore next -- publish() clears this timer, so it only ever fires while
           still unresolved; the guard is defensive and its else-path is unreachable */
        if (!this.state.resolved) {
          const cookieConsentState2: IStateCookieConsent = {
            resolved: true,
            categories: GRANTED_CATEGORIES,
            region: 'UNKNOWN',
            source: 'MANUAL',
            tcString: null,
          };
          this.publish(cookieConsentState2);
        }
      }, CMP_FALLBACK_MS);
    }
  }

  subscribe(listener: (state: IStateCookieConsent) => void): () => void {
    this.listeners.add(listener);
    // Replay the current state so late subscribers are never stale.
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Whether Funding Choices could show its revocation message; when not, the caller falls back to the first-party modal. */
  openPreferences(): boolean {
    return this.page.showRevocationMessage();
  }

  applyManualConsent(categories: IEntityCookieConsentCategories): void {
    // Persist first so the choice survives a reload (restored in init()).
    this.manualConsent.save(categories);
    const cookieConsentState3: IStateCookieConsent = {
      resolved: true,
      categories,
      region: this.state.region === 'UNKNOWN' ? 'MANUAL' : this.state.region,
      source: 'MANUAL',
      tcString: null,
    };
    this.publish(cookieConsentState3);
  }

  private registerTcfListener(): void {
    const tcfApi = this.page.tcfApi();
    if (!tcfApi) return;

    tcfApi('addEventListener', 2, (data, success) => {
      if (!success || !ConsentGateway.isResolved(data)) return;
      // Precedence: a genuine CMP user action (`useractioncomplete`) is
      // authoritative — it supersedes and clears any stored first-party choice.
      // A passive `tcloaded` replay must NOT clobber a choice the visitor made
      // in our first-party preferences modal, otherwise their Reject/Accept would
      // be silently overwritten on the next load.
      const userAction = data.eventStatus === 'useractioncomplete';
      if (!userAction && this.manualConsent.load()) return;
      if (userAction) this.manualConsent.clear();
      this.publish(ConsentGateway.stateOf(data));
    });
  }

  private injectFundingChoices(): void {
    if (!this.fundingChoicesPublisherId) return;
    if (this.page.hasElement(FC_SCRIPT_ID)) return;
    this.page.appendScript(FC_SCRIPT_ID, `https://fundingchoicesmessages.google.com/i/${this.fundingChoicesPublisherId}?ers=1`);
  }

  private publish(next: IStateCookieConsent): void {
    if (this.fallbackTimer !== null) {
      this.timers.clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
    this.state = next;
    this.tellGoogleTag(next.categories);
    for (const listener of this.listeners) {
      listener(next);
    }
  }

  /** Google Consent Mode: the categories, as an update on Google tag's data layer. */
  private tellGoogleTag(categories: IEntityCookieConsentCategories): void {
    this.page.dataLayer().push([
      'consent',
      'update',
      {
        ad_storage: categories.adStorage ? 'granted' : 'denied',
        ad_user_data: categories.adUserData ? 'granted' : 'denied',
        ad_personalization: categories.adPersonalization ? 'granted' : 'denied',
        analytics_storage: categories.analyticsStorage ? 'granted' : 'denied',
      },
    ]);
  }
}
